import { ChevronDown, Map, Images, CalendarDays, Mail, Play, ExternalLink } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuLabel, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { useDims } from "@/lib/units";
import { vt } from "@/lib/industry";

export function menuGroups(hotspots) {
  const groups = {};
  hotspots.forEach((h) => {
    const g = (h.menu_group || "").trim() || (h.kind === "part" ? "Equipment" : h.kind === "area" ? "Studio areas" : h.kind === "room" ? vt().roomGroup : vt().featGroup);
    (groups[g] = groups[g] || []).push(h);
  });
  return Object.entries(groups);
}

const Trigger = ({ children, testid }) => (
  <DropdownMenuTrigger className="h-16 px-3 xl:px-4 text-[11px] font-semibold uppercase tracking-[0.12em] text-white/70 hover:text-white data-[state=open]:text-white inline-flex items-center gap-1.5 whitespace-nowrap outline-none" data-testid={testid}>
    {children}<ChevronDown className="w-3 h-3 opacity-50" />
  </DropdownMenuTrigger>
);

const Content = ({ children }) => (
  <DropdownMenuContent align="start" sideOffset={0} className="min-w-[240px] max-h-[70vh] overflow-y-auto rounded-t-none rounded-b-2xl border-white/10 bg-[#0b1626]/95 backdrop-blur-xl text-white p-2">{children}</DropdownMenuContent>
);

const Item = ({ children, onSelect, testid }) => (
  <DropdownMenuItem onSelect={onSelect} className="rounded-xl px-3 py-2.5 cursor-pointer focus:bg-white/10 focus:text-white" data-testid={testid}>{children}</DropdownMenuItem>
);

export function TourMenus({ hotspots, onSelect, hasStops, onPlayTour, hasPlan, onFloor, hasGallery, onGallery, onBook, onRegister, presenter, extra = [], onLink, rank = () => 0 }) {
  const dims = useDims();
  return (
    <nav className="hidden lg:flex items-center justify-center flex-1 min-w-0 overflow-x-auto no-scrollbar" data-testid="tour-menus">
      {menuGroups(hotspots).map((g, i) => [g, i]).sort(([a, i], [b, j]) => rank(a[1][0].kind) - rank(b[1][0].kind) || i - j).map(([[g, items]]) => (
        <DropdownMenu key={g} modal={false}>
          <Trigger testid={`tour-menu-${g.toLowerCase().replace(/\W+/g, "-")}`}>{g}</Trigger>
          <Content>
            {items.map((h) => (
              <Item key={h.id} onSelect={() => onSelect(h)} testid={`tour-menu-item-${h.id}`}>
                <span className="flex-1">{h.title}</span>{h.dimensions && <span className="text-xs text-white/50 ml-3">{dims(h.dimensions)}</span>}
              </Item>
            ))}
          </Content>
        </DropdownMenu>
      ))}
      {(hasStops || hasPlan || hasGallery) && (
        <DropdownMenu modal={false}>
          <Trigger testid="tour-menu-explore">Explore</Trigger>
          <Content>
            {hasStops && <Item onSelect={onPlayTour} testid="tour-menu-guided"><Play className="w-4 h-4 mr-2" />Guided tour</Item>}
            {hasPlan && <Item onSelect={onFloor} testid="tour-menu-floorplan"><Map className="w-4 h-4 mr-2" />Floor plans</Item>}
            {hasGallery && <Item onSelect={onGallery} testid="tour-menu-gallery"><Images className="w-4 h-4 mr-2" />Gallery</Item>}
          </Content>
        </DropdownMenu>
      )}
      {!presenter && (
        <>
          <span className="w-px h-5 bg-white/10 mx-2" />
          <DropdownMenu modal={false}>
            <Trigger testid="tour-menu-contact">Contact</Trigger>
            <Content>
              <DropdownMenuLabel className="text-[10px] uppercase tracking-widest text-white/40">{vt().contactLabel}</DropdownMenuLabel>
              <Item onSelect={onBook} testid="tour-menu-book"><CalendarDays className="w-4 h-4 mr-2" />{vt().book}</Item>
              {extra.map((l, i) => <Item key={l.url} onSelect={() => onLink(l)} testid={`tour-menu-link-${i}`}><ExternalLink className="w-4 h-4 mr-2" />{l.label}</Item>)}
              <DropdownMenuSeparator className="bg-white/10" />
              <Item onSelect={onRegister} testid="tour-menu-register"><Mail className="w-4 h-4 mr-2" />Register interest</Item>
            </Content>
          </DropdownMenu>
        </>
      )}
    </nav>
  );
}


const Act = ({ icon: I, label, onClick, testid }) => (
  <button onClick={onClick} className="h-11 px-4 rounded-2xl bg-white/10 hover:bg-white/20 text-sm font-semibold inline-flex items-center gap-2 shrink-0" data-testid={testid}><I className="w-4 h-4" />{label}</button>
);

export function CompactActions({ hasStops, onPlayTour, hasPlan, onFloor, hasGallery, onGallery, onBook, onRegister, presenter, close, extra = [], onLink }) {
  const go = (fn) => () => { close(); fn(); };
  return (
    <div className="flex flex-wrap gap-2 mt-3" data-testid="compact-actions">
      {hasStops && <Act icon={Play} label="Guided tour" onClick={go(onPlayTour)} testid="compact-guided" />}
      {hasPlan && <Act icon={Map} label="Floor plans" onClick={go(onFloor)} testid="compact-floorplan" />}
      {hasGallery && <Act icon={Images} label="Gallery" onClick={go(onGallery)} testid="compact-gallery" />}
      {!presenter && <Act icon={CalendarDays} label={vt().book} onClick={go(onBook)} testid="compact-book" />}
      {!presenter && extra.map((l, i) => <Act key={l.url} icon={ExternalLink} label={l.label} onClick={go(() => onLink(l))} testid={`compact-link-${i}`} />)}
      {!presenter && <Act icon={Mail} label="Register interest" onClick={go(onRegister)} testid="compact-register" />}
    </div>
  );
}
